import Button from "components/Button";
import Modal from "components/Modal";
import { ImageUpdateModal1, ImageUpdateModal2 } from "images";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PATHS } from "router/config/paths";
import { useAppSelector } from "store";
import { hideUpdateModal } from "store/commonSlice";
import { AnalyticsEvent, getClearPath } from "utils";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Pagination, Autoplay } from "swiper/modules";
import { nanoid } from "@reduxjs/toolkit";
import clsx from "clsx";
import { useSiteImage } from "hooks/useSiteImage";

const INITIAL_SLIDE = 0;

const UpdateModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showUpdateModal } = useAppSelector((state) => state.common);
  const [activeIndex, setActiveIndex] = React.useState(INITIAL_SLIDE);
  const swiperRef = React.useRef<any>(null);
  const isUserInteractionRef = React.useRef<boolean>(false);
  const updateImage1 = useSiteImage("campaign.feature_update_1", ImageUpdateModal1);
  const updateImage2 = useSiteImage("campaign.feature_update_2", ImageUpdateModal2);

  React.useEffect(() => {
    setActiveIndex(INITIAL_SLIDE);
  }, [showUpdateModal]);

  const handleDotClick = (index: number) => {
    setActiveIndex(index);
    if (swiperRef.current) {
      isUserInteractionRef.current = true;
      swiperRef.current.slideTo(index);
    }
  };

  const translatedItems = t("common:modals.update.items", { returnObjects: true }) as { title: string; lines: string[] }[];
  const itemImages = [updateImage1, updateImage2];
  const items = translatedItems.map((entry, idx) => ({
    img: itemImages[idx],
    title: entry.title,
    descriptionLines: entry.lines,
  }));

  function onClose() {
    dispatch(hideUpdateModal());
  }

  return (
    <Modal onClose={onClose} show={showUpdateModal} bodyClassName="bg-black-100 !max-w-[480px] !border-black-300 z-50 h-full overflow-hidden overflow-y-auto no-scrollbar" className="!z-[9999]">
      <div className="flex flex-col gap-[23px] p-6 overflow-y-scroll">
        <h2 className="text-h2 text-white">{t("common:modals.update.title")}</h2>

        <Swiper
          slidesPerView={"auto"}
          centeredSlides={true}
          spaceBetween={15}
          grabCursor={true}
          onSlideChange={(swiper: SwiperClass) => {
            setActiveIndex(swiper.activeIndex);
            if (isUserInteractionRef.current) {
              AnalyticsEvent("homepage_carousel_scroll", "", "scroll", {
                screen_name: "UpdateModal",
                slide_index: swiper.activeIndex,
              });
              isUserInteractionRef.current = false;
            }
          }}
          onTouchStart={() => {
            isUserInteractionRef.current = true;
          }}
          onSliderMove={() => {
            isUserInteractionRef.current = true;
          }}
          modules={[Pagination, Autoplay]}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
          }}
          initialSlide={INITIAL_SLIDE}
          onSwiper={(swiper: any) => {
            swiperRef.current = swiper;
          }}
        >
          {items?.map((item: any) => {
            return (
              <SwiperSlide key={nanoid()}>
                <div className="flex flex-col gap-5">
                  <img src={item.img} />
                  <h3 className="text-h3 text-white">{item.title}</h3>
                  <div className="flex flex-col gap-5">
                    {item.descriptionLines.map((line: string) => (
                      <span key={nanoid()} className="text-bodyMd text-white">
                        {line}
                      </span>
                    ))}
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
          <div className="flex-center items-center w-full gap-[5px]">
            {items.map((_, index) => (
              <button key={nanoid()} className={clsx("rounded-full h-2 w-2", activeIndex === index ? "bg-green" : "bg-grey")} onClick={() => handleDotClick(index)} />
            ))}
          </div>
        </Swiper>

        <div className="flex flex-col gap-2">
          <Button
            className="btn-primary"
            onClick={() => {
              onClose();

              navigate(getClearPath(PATHS.PHOTOSHOOT));
            }}
          >
            {t("common:modals.update.try_now")}
          </Button>
          <Button className="btn-secondary" onClick={onClose}>
            {t("common:modals.update.close")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UpdateModal;
